import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import debounce from 'lodash/debounce';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';

import {Margin, ENV, ListViews, HeadlineLevel} from 'features/ui/enums';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {getCurrentEnv, getSiteSpecificConfig} from 'features/services/utils/env-helper';
import {Tag} from 'features/search/types';
import {sortTagsByCategory} from 'components/organisms/ResidenceSearch/Search/helpers';
import {SearchService} from 'features/search/services/SearchService';
import {useSearchStore, selectCurrentListView, selectSearchTags} from 'features/search/store/useSearchStore';

import './style.scss';

import {A} from 'components/atoms/LinksAndButtons/A';
import {Headline} from 'components/atoms/Text/Headline';
import {Icon} from 'components/atoms/Media/Icon';
import {SearchTag} from 'components/atoms/Tags/SearchTag';
import {Spinner} from 'components/atoms/Loaders/Loader/_spinner';
import {TagMenu} from 'components/organisms/ResidenceSearch/Search/_tagMenu';
import {PopularSearches} from 'components/organisms/ResidenceSearch/Search/_popularSearches';
import {TagsWithFoldout} from 'components/organisms/ResidenceSearch/Search/TagsWithFoldOut';
import {useTrapFocus} from "components/hooks/useTrapFocus";

import {useOutsideClick} from 'components/hooks/useOutsideClick';
import { SearchField } from './_searchField';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import { P } from '@atoms/Text/P';

interface Props {
    hideTitle?: boolean;
    fullWidth?: boolean;
    alternativeVersion?: boolean;
    inModal?: boolean;
    onMobileTap?: () => void;
}

export const Search: React.FunctionComponent<Props> = ({hideTitle, fullWidth, alternativeVersion, inModal, onMobileTap}) => {
    const env = getCurrentEnv();
    const selectedTags = useSearchStore(selectSearchTags);
    const currentListView = useSearchStore(selectCurrentListView);
    const addSearchTag = useSearchStore(state => state.addSearchTag);
    const removeSearchTag = useSearchStore(state => state.removeSearchTag);
    const isMobile = useDeviceStore(selectIsMobile);
    const siteId = getSiteSpecificConfig().siteId;

    const [activeTags, setActiveTags] = useState<{[key: string] : Tag[]}>({});
    const [searchString, setSearchString] = useState<string>('');
    const [showTagMenu, setShowTagMenu] = useState<boolean>(false);
    const [noTagResult, setNoTagResult] = useState<boolean>(false);
    const [animationTriggered, setAnimationTriggered] = useState<boolean>(false);
    const [hasFoldedTags, setHasFoldedTags] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);

    const searchFieldInput = useRef<HTMLInputElement>(null);
    const wrapper = useRef(null);
    const searchWrapperRef = useRef<HTMLDivElement>(null);
    const lastTagRef = useRef(null);
    const mobileTargetRef = useRef(null);

    const handleSearchDebounced = useRef(
        debounce(async (currentInputValue:string) => {
            const tagResponse = await SearchService.searchTagsByString(currentInputValue, {
                siteId: [siteId],
            });

            const tagHits = tagResponse.data?.hits ?? [];

            if(tagHits.length === 0) {
                setNoTagResult(true);
                const noHitResponse = await SearchService.searchStageCounties(siteId);
                const sortedTags: { [key: string]: Tag[] } = sortTagsByCategory(noHitResponse.data?.hits ?? [], currentInputValue);
                setActiveTags(sortedTags);

            } else {
                setNoTagResult(false);
                const sortedTags: { [key: string]: Tag[] } = sortTagsByCategory(tagHits, currentInputValue);
                setActiveTags(sortedTags);
            }

            setShowTagMenu(currentInputValue.length > 0);

        }, 300, {
            leading: false
        })
    ).current;

    useEffect(() => {
        if(searchFieldInput.current !== null) {
            searchFieldInput.current.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if(searchFieldInput.current !== null) {
                searchFieldInput.current.removeEventListener("keydown", onKeyDown);
            }
        }
    }, [searchString, selectedTags]);

    // Unmount
    useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
            document.body.classList.remove('mobile-search-open');
            if(env !== ENV.PROD && env !== ENV.PREPROD) {
                document.body.classList.remove('mobile-search-open--test');
            }
        }
    }, []);

    const onKeyDown = (e:KeyboardEvent) => {
        switch (e.key) {
            case 'Backspace':
                if(selectedTags.length > 0 && searchString.length === 0) {
                    e.preventDefault();
                    const lastTag = [...selectedTags].pop();

                    if (lastTag) {
                        removeSearchTag(lastTag);
                    }
                    showTemporaryLoader();
                }
                break;

            case 'Enter':
                if(isMobile && searchString.length === 0) {
                    //closeAnimation();
                }
                break;
        }
    };

    const showTemporaryLoader = () => {
        if(!isMobile) return;

        /*setShowLoader(true);

        setTimeout(() => {
            setShowLoader(false);
        }, 1000);*/
    };

    const onSearchFieldChange = async (e:React.ChangeEvent<HTMLInputElement>) => {
        const inputValue: string = e.target.value;

        setSearchString(inputValue);
        handleSearchDebounced(inputValue);
    };

    const onSearchFieldFocus = (_e:React.FocusEvent<HTMLInputElement>) => {
        if(!isMobile) return;

        /*setTimeout(() => {
            document.body.classList.add('mobile-search-open');
            if(env !== ENV.PROD && env !== ENV.PREPROD) {
                document.body.classList.add('mobile-search-open--test');
            }
            setAnimationTriggered(true);
            window.scrollTo(0, 0);
            disableBodyScroll(wrapper.current);
        }, 400);*/
    };

    // Focus the search field when mobile modal opens
    useEffect(() => {
        /*if (isMobile && animationTriggered) {
            // Use requestAnimationFrame to ensure DOM is ready
            const frameId = requestAnimationFrame(() => {
                focusField();
            });
            return () => cancelAnimationFrame(frameId);
        }*/
        return;
    }, [animationTriggered]);

    const onSelectTag = (tag: Tag) => {
        addSearchTag(tag);

        setSearchString('');
        closeTagMenu();
        closeAnimation();

        setTimeout(() => {
            showTemporaryLoader();
        });

        //if(isMobile) {
            //blurField();
        //} else {
            focusField();
        //}
    };

    const onRemoveTag = (tag: Tag) => {
        removeSearchTag(tag);
        showTemporaryLoader();
    };

    const closeTagMenu = () => {
        setShowTagMenu(false);
        closeAnimation();
    };

    useOutsideClick({
        refs: [searchWrapperRef], // Popupens referens
        callback: closeTagMenu, // Funktion för att stänga menyn
    });

    const closeAnimation = () => {
        setAnimationTriggered(false);
        document.body.classList.remove('mobile-search-open');
        if(env !== ENV.PROD && env !== ENV.PREPROD) {
            document.body.classList.remove('mobile-search-open--test');
        }
        clearAllBodyScrollLocks();
        blurField();
    };

    const blurField = () => {
        searchFieldInput.current?.blur();
    };

    const focusField = () => {
        searchFieldInput.current?.focus();
    };

    const onCancelClick = (e:React.MouseEvent) => {
        e.preventDefault();
        closeAnimation();
        setSearchString('');
    };

    const onWrapperClick = () => {
        // Mobile compact view: a tap anywhere in the search wrapper opens the
        // expanded search modal instead of focusing the underlying input.
        if (isMobile && !inModal && onMobileTap) {
            onMobileTap();
            return;
        }
        focusField();
        if(searchString.length > 0) setShowTagMenu(true);
    };

    const onWrapperMouseDown = (e: React.MouseEvent) => {
        // Prevent focus loss when clicking on wrapper elements (not the input itself)
        if (!(e.target instanceof HTMLInputElement)) {
            e.preventDefault();
        }
    };

    const onFoldChange = (isFolded: boolean) => {
        setHasFoldedTags(isFolded);
    };

    const onFoldedTagsFocus = () => {
        focusField();
    };

    useTrapFocus({
        triggerElementRef: isMobile ? mobileTargetRef : searchFieldInput,
        triggerElementId: "residence-search",
        firstFocusElement: searchFieldInput,
        lastFocusElement: lastTagRef,
        wrapperElement: searchWrapperRef,
        onCloseCallback: closeTagMenu,
        trigger: animationTriggered,
    });

    const searchClasses = classnames('residence-search small-headline', {
        //'main-grid': !animationTriggered,
        //'main-grid--full-width': !animationTriggered && fullWidth,
        'residence-search--animated': animationTriggered,
        'residence-search--expanded': inModal,
        'residence-search--closing': showLoader,
    });
    const tagWrapperClasses = classnames('tag-menu__wrapper', {
        'tag-menu__wrapper--hidden': searchString.length === 0, // || (isMobile && !animationTriggered),
    });
    const searchFieldClasses = classnames('search-field', {
        'search-field--alternative': alternativeVersion,
    });

    return (
        <div className={searchClasses} ref={searchWrapperRef}>
            {((animationTriggered || !hideTitle) && (currentListView !== ListViews.MAP_VIEW || animationTriggered)) &&
                <Headline 
                    level={HeadlineLevel.H1}
                    center 
                    margin={Margin.S} 
                >
                    <FormattedMessage id="jm.components.SearchField.whereToLive" />
                    {isMobile && animationTriggered &&
                        <A
                            href="#"
                            onClick={onCancelClick}
                            className="residence-search__cancel"
                            title={getFormattedMessage('jm.components.MobileModal.cancel')}
                        >
                            <Icon name="x" size={24} />
                        </A>
                    }
                </Headline>
            }

            <div className={searchFieldClasses} onClick={onWrapperClick} onMouseDown={onWrapperMouseDown}>
                {showLoader
                    ? <Spinner small/>
                    : <Icon name="looking-glas"/>
                }

                <div className="search-field__content">
                    {!isMobile &&
                        <TagsWithFoldout
                            onChange={onFoldChange}
                            focus={onFoldedTagsFocus}
                            breakWidth={(isMobile) ? 200 : /*(currentListView === ListViews.MAP_VIEW) ? 250 : 400*/ 300}
                        >
                            {hasFoldedTags &&
                                <SearchField
                                    ref={searchFieldInput}
                                    searchString={searchString}
                                    onSearchFieldChange={onSearchFieldChange}
                                    onSearchFieldFocus={onSearchFieldFocus}
                                    animationTriggered={animationTriggered}
                                />
                            }

                            <div ref={wrapper} className={tagWrapperClasses}>
                                {hasFoldedTags && showTagMenu &&
                                    <TagMenu
                                        activeTags={activeTags}
                                        searchString={searchString}
                                        onSelectTag={onSelectTag}
                                        emptyResult={noTagResult}
                                    />
                                }
                            </div>
                        </TagsWithFoldout>
                    }

                    {isMobile && selectedTags.length > 0 && !(animationTriggered || inModal) &&
                        <div className="search-field__mobile-tags search-field__mobile-tags--unanimated">
                            {selectedTags.length > 0 &&
                                <SearchTag
                                    tag={selectedTags[0]}
                                    onClick={onWrapperClick}
                                    ghost
                                    hideRemove={isMobile}
                                />
                            }
                            {selectedTags.length > 1 &&
                                <SearchTag
                                    tag={{
                                        id: 'fold-out',
                                        name: `+ ${selectedTags.length - 1}`,
                                        type: 'fold',
                                    }}
                                    onClick={onWrapperClick}
                                    ghost
                                    hideRemove={isMobile}
                                />
                            }
                        </div>
                    }

                    {isMobile && selectedTags.length > 0 && (animationTriggered || inModal) &&
                        selectedTags.map((tag, index) => {
                            return (
                                <SearchTag
                                    tag={tag}
                                    ghost
                                    key={`tag-folded-${index}`}
                                    onRemove={onRemoveTag}
                                />
                            )
                        })
                    }

                    {!hasFoldedTags  &&
                        <SearchField
                            key="residence-search-field"
                            ref={searchFieldInput}
                            searchString={searchString}
                            onSearchFieldChange={onSearchFieldChange}
                            onSearchFieldFocus={onSearchFieldFocus}
                            animationTriggered={animationTriggered}
                        />
                    }
                </div>
            </div>

            <div ref={wrapper} className={tagWrapperClasses}>
                {!hasFoldedTags && showTagMenu &&
                    <TagMenu
                        activeTags={activeTags}
                        searchString={searchString}
                        onSelectTag={onSelectTag}
                        emptyResult={noTagResult}
                    />
                }
            </div>

            {!showTagMenu && (animationTriggered || inModal) &&
                <PopularSearches
                    onTagClick={onSelectTag}
                    ref={lastTagRef}
                />
            }
            
            <div ref={mobileTargetRef} />
        </div>
    )
};
